import React, { useState } from 'react';
import { TrendingUpIcon, UsersIcon, MailOpenIcon, MessageSquareIcon, PhoneOffIcon, StarIcon, MapPinIcon, CheckCircle2Icon, ArrowRightIcon, SparklesIcon, BarChart3Icon, FilterIcon, XIcon, SendIcon, MousePointerClickIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { getCategoryLabel } from '../data/communications';
import type { ChannelKey, Notice } from '../types/notice';
const CHANNEL_LABELS: Record<ChannelKey, {
  fr: string;
  en: string;
}> = {
  email: {
    fr: 'Courriel',
    en: 'Email'
  },
  sms: {
    fr: 'SMS',
    en: 'SMS'
  },
  voice: {
    fr: 'Voix automatisée',
    en: 'Automated voice'
  },
  push: {
    fr: 'Notification Push',
    en: 'Push notification'
  },
  website: {
    fr: 'Site web',
    en: 'Website'
  },
  social: {
    fr: 'Réseaux sociaux',
    en: 'Social media'
  },
  screens: {
    fr: 'Écrans publics',
    en: 'Public screens'
  },
  app: {
    fr: 'Application citoyenne',
    en: 'Citizen app'
  }
};
interface AnalyticsDashboardProps {
  /** When set, the dashboard reports on this single notice. */
  focusedNotice?: Notice | null;
  onClearFocus?: () => void;
}

/** Compact metric tile shared by both dashboard modes. */
function MetricCard({
  icon: Icon,
  label,
  value,
  hint,
  hintTone = 'muted',
  pending
}: {
  icon: typeof TrendingUpIcon;
  label: string;
  value: string;
  hint?: string;
  hintTone?: 'muted' | 'positive';
  /** Replaces the figure with a "available once sent" placeholder. */
  pending?: string;
}) {
  return <div className={`p-5 rounded-xl shadow-sm border ${pending ? 'bg-slate-50 border-slate-200' : 'bg-white border-slate-100'}`} data-id="element-48" data-mp="1o6ay8h5bzcdo">
      <div className="flex items-center gap-3 mb-2" data-id="element-49" data-mp="1a836qor2d39w">
        <div className={`p-2 rounded-lg ${pending ? 'bg-slate-200 text-slate-400' : 'bg-indigo-50 text-[#4F46E5]'}`} data-id="element-50" data-mp="1e6twgat79imz">
          <Icon className="w-5 h-5" data-id="element-51" data-mp="38lmmlubbdh7j" />
        </div>
        <h3 className="text-sm font-medium text-slate-600" data-id="element-52" data-mp="38kblk2996nk4">{label}</h3>
      </div>
      {pending ? <p className="text-sm text-slate-400 italic pt-1.5" data-id="element-53" data-mp="ldyrizoyqxl0">{pending}</p> : <div className="flex items-end gap-3 flex-wrap" data-id="element-54" data-mp="gd168e8npnfa">
          <span className="text-3xl font-bold text-[#0F172A] font-heading" data-id="element-55" data-mp="cchbtifgwxwu">
            {value}
          </span>
          {hint && <span className={`text-sm mb-1 ${hintTone === 'positive' ? 'font-medium text-emerald-600' : 'text-slate-500'}`} data-id="element-56" data-mp="7eft4ylyf0lw">
              {hint}
            </span>}
        </div>}
    </div>;
}
export function AnalyticsDashboard({
  focusedNotice = null,
  onClearFocus
}: AnalyticsDashboardProps) {
  const {
    t,
    lang
  } = useLanguage();
  const [selectedSector, setSelectedSector] = useState('A');
  const locale = lang === 'fr' ? 'fr-CA' : 'en-CA';

  // ---------------------------------------------------------------- FOCUSED
  if (focusedNotice) {
    const stats = focusedNotice.stats;
    // Not sent yet: the layout still shows, with each figure marked as pending.
    const pending = stats ? undefined : t('Disponible après l’envoi', 'Available once sent');
    return <div className="space-y-8 animate-in fade-in duration-300" data-id="element-57" data-mp="2096ywtq4zbpb">
        <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-5 flex flex-col sm:flex-row sm:items-center gap-4 justify-between" data-id="element-58" data-mp="24blijg1mqfhn">
          <div className="min-w-0" data-id="element-59" data-mp="29a56flzsa1cn">
            <div className="flex items-center gap-2 flex-wrap mb-1.5" data-id="element-60" data-mp="2993s98nh0yqp">
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-white text-[#4F46E5] text-xs font-semibold border border-indigo-200" data-id="element-61" data-mp="2e8gfvvm629uc">
                <FilterIcon className="w-3 h-3" data-id="element-62" data-mp="2o0413es4qund" />
                {t('Filtré sur un avis', 'Filtered on one notice')}
              </span>
              <span className="px-2 py-0.5 rounded-full bg-white text-slate-600 text-xs font-medium border border-slate-200" data-id="element-63" data-mp="2s392fe0h4qaf">
                {getCategoryLabel(focusedNotice.category)[lang]}
              </span>
              <span className="text-xs text-slate-500" data-id="element-64" data-mp="2jbpcqlvc9p5u">{focusedNotice.id}</span>
            </div>
            <h2 className="text-lg font-bold text-[#0F172A] font-heading truncate" data-id="element-65" data-mp="2fa44jdtr1z1z">
              {focusedNotice.title}
            </h2>
            <p className="text-sm text-slate-500 mt-0.5" data-id="element-66" data-mp="2o4s54qodqskg">
              {focusedNotice.status === 'scheduled' ? t('Envoi prévu le ', 'Sending on ') : focusedNotice.status === 'draft' ? t('Brouillon modifié le ', 'Draft edited on ') : t('Envoyé le ', 'Sent on ')}
              {new Date(focusedNotice.date).toLocaleDateString(locale, {
              day: 'numeric',
              month: 'long',
              year: 'numeric'
            })}
              {focusedNotice.audience.detail ? ` · ${focusedNotice.audience.detail[lang]}` : ''}
            </p>
          </div>
          <button onClick={onClearFocus} className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors" data-id="element-67" data-mp="3gvk41vwtxvbh">
            <XIcon className="w-4 h-4" data-id="element-68" data-mp="tm1plreuvrmz" />
            {t('Voir tout le module', 'View the whole module')}
          </button>
        </div>

        {pending && <p className="flex items-start gap-2 text-sm text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-4 py-3" data-id="element-69" data-mp="1m9nr8u4iwm77">
            <BarChart3Icon className="w-4 h-4 mt-0.5 text-slate-400 flex-shrink-0" data-id="element-70" data-mp="1q9y1wk39g0se" />
            {t('Cet avis n’a pas encore été diffusé. L’audience et les canaux sont déjà connus ; les mesures de performance apparaîtront après l’envoi.', 'This notice has not gone out yet. Its audience and channels are already known; performance figures will appear once sent.')}
          </p>}

        <>
            <section data-id="element-71" data-mp="2a0tbrmles2nu">
              <h3 className="text-lg font-bold text-[#0F172A] font-heading mb-4 flex items-center gap-2" data-id="element-72" data-mp="2jvicw0f8nuzr">
                <BarChart3Icon className="w-5 h-5 text-[#4F46E5]" data-id="element-73" data-mp="2evcdfy2q9474" />
                {t('Performance de cet avis', 'Performance of this notice')}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-74" data-mp="29ym0vkq4sqqg">
                <MetricCard icon={SendIcon} label={t('Destinataires', 'Recipients')} value={stats ? stats.recipients.toLocaleString(locale) : focusedNotice.audience.count.toLocaleString(locale)} hint={stats ? `${stats.delivered.toLocaleString(locale)} ${t('livrés', 'delivered')}` : t('audience ciblée', 'targeted audience')} data-id="element-75" data-mp="24xg3vapfuhb4" />
                <MetricCard icon={MailOpenIcon} label={t("Taux d'ouverture", 'Open rate')} value={stats ? `${stats.openRate} %` : '—'} hint={t('tous canaux confondus', 'across all channels')} pending={pending} data-id="element-76" data-mp="1d71xs0no29a0" />
                <MetricCard icon={MousePointerClickIcon} label={t('Taux de clics', 'Click rate')} value={stats ? `${stats.clickRate} %` : '—'} hint={t('sur les liens de l’avis', 'on the notice links')} pending={pending} data-id="element-77" data-mp="1n33o5ne38yn" />
                <MetricCard icon={MessageSquareIcon} label={t('Interactions citoyennes', 'Citizen interactions')} value={stats ? stats.interactions.toLocaleString(locale) : '—'} hint={t('clics, réponses, questions', 'clicks, replies, questions')} pending={pending} data-id="element-78" data-mp="3f4c7kj7df319" />
                <MetricCard icon={PhoneOffIcon} label={t('Appels évités', 'Calls avoided')} value={stats ? stats.callsAvoided.toLocaleString(locale) : '—'} hint={t('estimé par ATNA', 'estimated by ATNA')} pending={pending} data-id="element-79" data-mp="2rceu6no9w2y1" />
                <MetricCard icon={StarIcon} label={t('Satisfaction', 'Satisfaction')} value={stats ? `${stats.satisfaction.toLocaleString(locale)} / 5` : '—'} hint={t('sur cet avis', 'for this notice')} pending={pending} data-id="element-80" data-mp="1ot0st54hyt53" />
              </div>
            </section>

            <section className="bg-white p-6 rounded-xl shadow-sm border border-slate-100" data-id="element-81" data-mp="2ckfz18ze9ulm">
              <h3 className="text-lg font-bold text-[#0F172A] font-heading mb-6" data-id="element-82" data-mp="28iunps2idodj">
                {t('Performance par canal', 'Performance by channel')}
              </h3>
              <div className="overflow-x-auto" data-id="element-83" data-mp="2hir3t5au1ewz">
                <table className="w-full text-sm text-left" data-id="element-84" data-mp="31384krrmcqk8">
                  <thead className="text-xs text-slate-500 uppercase bg-slate-50" data-id="element-85" data-mp="313rh1syi2mqv">
                    <tr data-id="element-86" data-mp="360zz7tjneqs4">
                      <th className="px-4 py-3 rounded-l-lg" data-id="element-87" data-mp="3608cz9bxy5ei">
                        {t('Canal', 'Channel')}
                      </th>
                      <th className="px-4 py-3" data-id="element-88" data-mp="2w3vc9rhzara9">
                        {t('Messages envoyés', 'Messages sent')}
                      </th>
                      <th className="px-4 py-3" data-id="element-89" data-mp="2ib5mdbzbx27v">
                        {t("Taux d'ouverture", 'Open rate')}
                      </th>
                      <th className="px-4 py-3 rounded-r-lg" data-id="element-90" data-mp="2n59rdpp1b06b">
                        {t('Taux de clics', 'Click rate')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100" data-id="element-91" data-mp="2dbat51s300jn">
                    {stats ? stats.channels.map(c => <tr key={c.channel} data-id="element-92" data-mp="23eemz2pnnqi9">
                            <td className="px-4 py-3 font-medium text-slate-900" data-id="element-93" data-mp="23hahsi67hz2h">
                              {CHANNEL_LABELS[c.channel][lang]}
                            </td>
                            <td className="px-4 py-3 text-slate-600" data-id="element-94" data-mp="2s9oe0mtohgxz">
                              {c.sent > 0 ? c.sent.toLocaleString(locale) : '—'}
                            </td>
                            <td className="px-4 py-3" data-id="element-95" data-mp="2iapd5qf5mplj">
                              <div className="flex items-center gap-2" data-id="element-96" data-mp="3601gwarpq5gg">
                                <span className="w-10" data-id="element-97" data-mp="36248gz8ik6na">{c.openRate} %</span>
                                <div className="w-24 h-2 bg-slate-100 rounded-full overflow-hidden" data-id="element-98" data-mp="3ay32p3giwivm">
                                  <div className="h-full bg-[#4F46E5]" style={{
                          width: `${c.openRate}%`
                        }} data-id="element-99" data-mp="2x77qbh6tfjna" />
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3" data-id="element-100" data-mp="3tr70n10rxhw1">
                              <div className="flex items-center gap-2" data-id="element-101" data-mp="1i92fao77cha2">
                                <span className="w-10" data-id="element-102" data-mp="1dbtwqmi56b8a">{c.clickRate} %</span>
                                <div className="w-24 h-2 bg-slate-100 rounded-full overflow-hidden" data-id="element-103" data-mp="1s06t3kl55w3b">
                                  <div className="h-full bg-emerald-500" style={{
                          width: `${c.clickRate}%`
                        }} data-id="element-104" data-mp="1n13m3ullpfsp" />
                                </div>
                              </div>
                            </td>
                          </tr>) : focusedNotice.channels.map(c => <tr key={c} data-id="element-105" data-mp="149kk9r3bc7s9">
                            <td className="px-4 py-3 font-medium text-slate-900" data-id="element-106" data-mp="14coy7g6kyhw4">
                              {CHANNEL_LABELS[c][lang]}
                            </td>
                            <td className="px-4 py-3 text-slate-400 italic" colSpan={3} data-id="element-107" data-mp="2fozkd3qtjfrl">
                              {pending}
                            </td>
                          </tr>)}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="bg-white p-6 rounded-xl shadow-sm border border-slate-100" data-id="element-108" data-mp="31ox2k2vkd9q3">
              <h3 className="text-lg font-bold text-[#0F172A] font-heading mb-4" data-id="element-109" data-mp="243ivd3cbwuj7">
                {t('Audience ciblée', 'Targeted audience')}
              </h3>
              <div className="flex items-center gap-3 flex-wrap" data-id="element-110" data-mp="2e0nmlhl17g6v">
                <span className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-sm text-slate-700" data-id="element-111" data-mp="2dzcj7ivi0aga">
                  <MapPinIcon className="w-4 h-4 text-slate-400" data-id="element-112" data-mp="1lchzio6i9f3v" />
                  {focusedNotice.audience.detail?.[lang] ?? t("Toute l'organisation", 'Entire organization')}
                </span>
                <span className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-sm text-slate-700" data-id="element-113" data-mp="7ex5sly8v8h4">
                  <UsersIcon className="w-4 h-4 text-slate-400" data-id="element-114" data-mp="3cinq42egcqw" />
                  {focusedNotice.audience.count.toLocaleString(locale)}{' '}
                  {t('citoyens', 'citizens')}
                </span>
                {focusedNotice.channels.map(c => <span key={c} className="px-3 py-2 rounded-lg bg-indigo-50 border border-indigo-100 text-sm text-[#4F46E5] font-medium" data-id="element-115" data-mp="3kn9xxs7pd50c">
                    {CHANNEL_LABELS[c][lang]}
                  </span>)}
              </div>
            </section>
        </>
      </div>;
  }

  // ----------------------------------------------------------------- GLOBAL
  const sectors = [{
    id: 'A',
    status: 'green',
    citizens: 12450,
    cards: 9820,
    openRate: 78,
    notices: 45,
    questions: 342
  }, {
    id: 'B',
    status: 'yellow',
    citizens: 8210,
    cards: 5100,
    openRate: 62,
    notices: 38,
    questions: 512
  }, {
    id: 'C',
    status: 'green',
    citizens: 15300,
    cards: 13900,
    openRate: 85,
    notices: 52,
    questions: 215
  }, {
    id: 'D',
    status: 'red',
    citizens: 6800,
    cards: 2850,
    openRate: 45,
    notices: 25,
    questions: 890
  }, {
    id: 'E',
    status: 'yellow',
    citizens: 9100,
    cards: 5200,
    openRate: 55,
    notices: 30,
    questions: 420
  }];
  const sectorName = (id: string) => `${t('Secteur', 'District')} ${id}`;
  const active = sectors.find(s => s.id === selectedSector) ?? sectors[0];
  const channelRows: {
    channel: ChannelKey | 'atna';
    sent: string;
    open: number;
    interact: number;
    best?: boolean;
  }[] = [{
    channel: 'push',
    sent: '85,420',
    open: 88,
    interact: 24,
    best: true
  }, {
    channel: 'sms',
    sent: '42,100',
    open: 92,
    interact: 18
  }, {
    channel: 'email',
    sent: '112,500',
    open: 45,
    interact: 8
  }, {
    channel: 'voice',
    sent: '8,400',
    open: 75,
    interact: 5
  }, {
    channel: 'atna',
    sent: '15,200',
    open: 100,
    interact: 42
  }, {
    channel: 'website',
    sent: '—',
    open: 35,
    interact: 12
  }, {
    channel: 'social',
    sent: '—',
    open: 22,
    interact: 6
  }];
  const funnel = [{
    label: t('Avis créés', 'Notices created'),
    value: '1,245',
    percent: 100,
    color: 'bg-slate-800'
  }, {
    label: t('Avis envoyés', 'Notices sent'),
    value: '125,842',
    percent: 95,
    color: 'bg-[#4F46E5]'
  }, {
    label: t('Ouverts', 'Opened'),
    value: '91,864',
    percent: 73,
    color: 'bg-indigo-400'
  }, {
    label: t('Cliqués', 'Clicked'),
    value: '34,210',
    percent: 27,
    color: 'bg-indigo-300'
  }, {
    label: t('Questions posées', 'Questions asked'),
    value: '8,450',
    percent: 6.7,
    color: 'bg-emerald-400'
  }, {
    label: t('Actions complétées', 'Actions completed'),
    value: '5,120',
    percent: 4.1,
    color: 'bg-emerald-500'
  }];
  const topics = [{
    topic: t('Déneigement', 'Snow removal'),
    sent: '45k',
    open: '88%',
    click: '34%',
    q: 1240
  }, {
    topic: t('Collecte des déchets', 'Waste collection'),
    sent: '112k',
    open: '75%',
    click: '12%',
    q: 890
  }, {
    topic: t('Travaux routiers', 'Road works'),
    sent: '28k',
    open: '82%',
    click: '41%',
    q: 2100
  }, {
    topic: t("Restrictions d'eau", 'Water restrictions'),
    sent: '15k',
    open: '91%',
    click: '22%',
    q: 450
  }, {
    topic: t('Taxes', 'Taxes'),
    sent: '85k',
    open: '65%',
    click: '18%',
    q: 1850
  }, {
    topic: t('Permis', 'Permits'),
    sent: '12k',
    open: '58%',
    click: '25%',
    q: 920
  }, {
    topic: t('Loisirs', 'Recreation'),
    sent: '65k',
    open: '42%',
    click: '15%',
    q: 310
  }, {
    topic: t('Bibliothèques', 'Libraries'),
    sent: '32k',
    open: '38%',
    click: '8%',
    q: 120
  }];
  const questions = [t('Quand est ma prochaine collecte ?', 'When is my next collection?'), t('Fermetures de routes', 'Road closures'), t('Règles de stationnement', 'Parking rules'), t('Taxes municipales', 'Municipal taxes'), t('Demande de permis', 'Permit application'), t('Piscines publiques', 'Public pools'), t('Services de bibliothèque', 'Library services'), t('Gestion des matières résiduelles', 'Waste management'), t("Avis d'urgence", 'Emergency notices'), t('Programmes de loisirs', 'Recreation programs')];
  return <div className="space-y-8 animate-in fade-in duration-300" data-id="element-116" data-mp="1w2uxyp3g7z1c">
      {/* Executive summary */}
      <section data-id="element-117" data-mp="1s19jizb9ncp1">
        <div className="flex items-center justify-between mb-4 gap-4 flex-wrap" data-id="element-118" data-mp="1s1rkuacsch3t">
          <h2 className="text-lg font-bold text-[#0F172A] font-heading flex items-center gap-2" data-id="element-119" data-mp="2orip31avpimt">
            <BarChart3Icon className="w-5 h-5 text-[#4F46E5]" data-id="element-120" data-mp="2jsfjnfqzlaeb" />
            {t('Sommaire exécutif', 'Executive summary')}
          </h2>
          <div className="flex items-center gap-2 text-sm text-slate-500" data-id="element-121" data-mp="2f0nu98jkhapr">
            <FilterIcon className="w-4 h-4" data-id="element-122" data-mp="2b0nftnwgwzip" />
            <span data-id="element-123" data-mp="2azm0h78f5qv6">{t('30 derniers jours', 'Last 30 days')}</span>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-124" data-mp="37jl1g0ghmcr9">
          <MetricCard icon={TrendingUpIcon} label={t('Avis envoyés', 'Notices sent')} value="125,842" hint={t('+18 % vs mois préc.', '+18% vs. prev. month')} hintTone="positive" data-id="element-125" data-mp="32q8owop31wev" />
          <MetricCard icon={UsersIcon} label={t('Citoyens atteints', 'Citizens reached')} value="91 %" hint={t('des citoyens inscrits', 'of registered citizens')} data-id="element-126" data-mp="3r7veotf2zn4k" />
          <MetricCard icon={MailOpenIcon} label={t("Taux d'ouverture", 'Open rate')} value="73 %" hint={t('tous canaux confondus', 'across all channels')} data-id="element-127" data-mp="90p73p6rzpij" />
          <MetricCard icon={MessageSquareIcon} label={t('Interactions citoyennes', 'Citizen interactions')} value="12,487" hint={t('clics, réponses, questions', 'clicks, replies, questions')} data-id="element-128" data-mp="3v8f7xlwja6lf" />
          <MetricCard icon={PhoneOffIcon} label={t('Appels évités', 'Calls avoided')} value="2,134" hint={t('estimé par ATNA', 'estimated by ATNA')} data-id="element-129" data-mp="2jul9sjxo9jtz" />
          <MetricCard icon={StarIcon} label={t('Satisfaction', 'Satisfaction')} value="4.7 / 5" hint={t('communications', 'communications')} data-id="element-130" data-mp="16vrsa22vkcsn" />
        </div>
      </section>

      {/* Recommendations & value */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-131" data-mp="1btjh2n883ism">
        <section className="lg:col-span-2" data-id="element-132" data-mp="o2nuc4imjds8">
          <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-6 h-full relative overflow-hidden" data-id="element-133" data-mp="o3p5e8zn409y">
            <div className="absolute top-0 right-0 p-6 opacity-10" data-id="element-134" data-mp="j2jbi7u8u6yu">
              <SparklesIcon className="w-32 h-32 text-[#4F46E5]" data-id="element-135" data-mp="wxbyfrcgqpfg" />
            </div>
            <div className="relative z-10" data-id="element-136" data-mp="s3zl4dd8i520">
              <div className="flex items-center gap-2 mb-4" data-id="element-137" data-mp="s37rh8376hed">
                <SparklesIcon className="w-6 h-6 text-[#4F46E5]" data-id="element-138" data-mp="4e5am2ge0skx" />
                <h2 className="text-xl font-bold text-[#0F172A] font-heading" data-id="element-139" data-mp="eed7fwcve7f">
                  {t('Recommandations ATNA', 'ATNA recommendations')}
                </h2>
              </div>

              <div className="bg-white/80 backdrop-blur-sm rounded-lg p-5 border border-indigo-100/50 mb-5" data-id="element-140" data-mp="2mjrhfblz13tp">
                <p className="text-slate-700 font-medium mb-1" data-id="element-141" data-mp="30bfrzagknk8k">
                  {t('Le Secteur A montre une forte portée numérique (83 %).', 'District A shows strong digital reach (83%).')}
                </p>
                <p className="text-slate-600 text-sm mb-4" data-id="element-142" data-mp="3aaws7cd215x5">
                  {t("Les requêtes concernant l'entretien routier sont en hausse dans cette zone.", 'Requests about road maintenance are rising in this area.')}
                </p>

                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2" data-id="element-143" data-mp="1ovr3ufrl0f66">
                  {t('Action recommandée', 'Recommended action')}
                </h4>
                <ul className="space-y-2 mb-4" data-id="element-144" data-mp="1jyqzqaealmhp">
                  {[t('Envoyer un avis ciblé', 'Send a targeted notice'), t('Utiliser Push + SMS', 'Use Push + SMS'), t('Notifier uniquement le secteur affecté', 'Notify only the affected district')].map(item => <li key={item} className="flex items-center gap-2 text-sm text-slate-700" data-id="element-145" data-mp="3cti2rxr3bjhf">
                      <CheckCircle2Icon className="w-4 h-4 text-emerald-500" data-id="element-146" data-mp="3hu62r8xarans" />
                      {item}
                    </li>)}
                </ul>

                <div className="flex items-center gap-4 p-3 bg-emerald-50 rounded-lg border border-emerald-100" data-id="element-147" data-mp="37tnfs5afre1n">
                  <div className="flex-1" data-id="element-148" data-mp="122rzbb2je7ff">
                    <div className="text-xs font-bold text-emerald-800 uppercase tracking-wider mb-1" data-id="element-149" data-mp="11zm7ho2iwrg0">
                      {t('Impact prévu', 'Expected impact')}
                    </div>
                    <div className="flex gap-4 text-sm font-medium text-emerald-700 flex-wrap" data-id="element-150" data-mp="2exmhv3ug96yo">
                      <span data-id="element-151" data-mp="2a30dvlqp6gom">
                        ↓ 22 % {t("d'appels entrants", 'inbound calls')}
                      </span>
                      <span data-id="element-152" data-mp="210r796yjv7gi">↑ 18 % {t('de satisfaction', 'satisfaction')}</span>
                    </div>
                  </div>
                </div>
              </div>

              <button className="flex items-center gap-2 bg-[#4F46E5] hover:bg-[#4338CA] text-white px-5 py-2.5 rounded-lg font-medium transition-colors shadow-sm shadow-indigo-200" data-id="element-153" data-mp="2xsb9kot6xkvw">
                {t("Préparer l'avis citoyen", 'Prepare citizen notice')}
                <ArrowRightIcon className="w-4 h-4" data-id="element-154" data-mp="2jy0oqikbeav4" />
              </button>
            </div>
          </div>
        </section>

        <section className="lg:col-span-1" data-id="element-155" data-mp="3hfsi8meevjlh">
          <div className="bg-[#0F172A] rounded-xl p-6 h-full text-white shadow-lg relative overflow-hidden" data-id="element-156" data-mp="3cn7plo78rcjj">
            <h2 className="text-xl font-bold font-heading mb-6" data-id="element-157" data-mp="3jzp55vzykd8h">
              {t('Valeur générée par bciti+', 'Value generated by bciti+')}
            </h2>

            <div className="space-y-5 relative z-10" data-id="element-158" data-mp="3oztm1n4s39z8">
              <div data-id="element-159" data-mp="3tw0lqy6kzp8q">
                <div className="text-3xl font-bold text-[#818CF8] mb-1" data-id="element-160" data-mp="3tvs4yv7c7zs9">
                  87 000 $
                </div>
                <div className="text-sm text-slate-300" data-id="element-161" data-mp="1qy1gxpowomh">
                  {t('Économies opérationnelles estimées', 'Estimated operational savings')}
                </div>
              </div>

              <div className="h-px bg-slate-700/50" data-id="element-162" data-mp="gj8abykri2sf" />

              <ul className="space-y-3" data-id="element-163" data-mp="lha2ra42vegk">
                {[{
                label: t('Appels évités', 'Calls avoided'),
                value: '2,134'
              }, {
                label: t('Courriels évités', 'Emails avoided'),
                value: '847'
              }, {
                label: t('Visites au comptoir évitées', 'Counter visits avoided'),
                value: '319'
              }, {
                label: t('Heures de travail sauvées', 'Work hours saved'),
                value: '147 h'
              }].map(row => <li key={row.label} className="flex items-center justify-between gap-3" data-id="element-164" data-mp="qn55zwoen38p">
                    <span className="text-sm text-slate-300" data-id="element-165" data-mp="lmptf7by5zrs">{row.label}</span>
                    <span className="font-bold" data-id="element-166" data-mp="3u2j1qyw4k4xr">{row.value}</span>
                  </li>)}
              </ul>
            </div>
          </div>
        </section>
      </div>

      {/* Channel performance */}
      <section className="bg-white p-6 rounded-xl shadow-sm border border-slate-100" data-id="element-167" data-mp="7vukjv1g7io7">
        <h2 className="text-lg font-bold text-[#0F172A] font-heading mb-6" data-id="element-168" data-mp="2tvgeixvwhq7">
          {t('Performance par canal', 'Performance by channel')}
        </h2>
        <div className="overflow-x-auto" data-id="element-169" data-mp="1eb4wqwlputna">
          <table className="w-full text-sm text-left" data-id="element-170" data-mp="1ea3jclh89v2e">
            <thead className="text-xs text-slate-500 uppercase bg-slate-50" data-id="element-171" data-mp="2w7qhv5qitb8n">
              <tr data-id="element-172" data-mp="317bgtg5oxjld">
                <th className="px-4 py-3 rounded-l-lg" data-id="element-173" data-mp="317un1zlzajjk">{t('Canal', 'Channel')}</th>
                <th className="px-4 py-3" data-id="element-174" data-mp="2n7jxczwenazz">{t('Messages envoyés', 'Messages sent')}</th>
                <th className="px-4 py-3" data-id="element-175" data-mp="2n9wlxvwo4jm0">{t("Taux d'ouverture", 'Open rate')}</th>
                <th className="px-4 py-3 rounded-r-lg" data-id="element-176" data-mp="2s6wt6a597sep">
                  {t("Taux d'interaction", 'Interaction rate')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-177" data-mp="3jwj76jcn0kua">
              {channelRows.map(row => <tr key={row.channel} className={row.best ? 'bg-indigo-50/30' : ''} data-id="element-178" data-mp="365wi7iwoi9be">
                  <td className="px-4 py-3 font-medium text-slate-900" data-id="element-179" data-mp="3b4h92rq1jgn6">
                    <span className="flex items-center gap-2" data-id="element-180" data-mp="2p5vttgshjnj">
                      {row.channel === 'atna' ? t('Assistant ATNA', 'ATNA assistant') : CHANNEL_LABELS[row.channel][lang]}
                      {row.best && <span className="px-2 py-0.5 text-[10px] bg-[#4F46E5] text-white rounded-full font-bold" data-id="element-181" data-mp="ha1w312rcevy">
                          {t('MEILLEUR', 'BEST')}
                        </span>}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-600" data-id="element-182" data-mp="2elxqjv59wtr">{row.sent}</td>
                  <td className="px-4 py-3" data-id="element-183" data-mp="3uj6ed2n1ngy2">
                    <div className="flex items-center gap-2" data-id="element-184" data-mp="3ul0ywfuel71m">
                      <span className="w-10" data-id="element-185" data-mp="3pr6efa6z4o1h">{row.open} %</span>
                      <div className="w-24 h-2 bg-slate-100 rounded-full overflow-hidden" data-id="element-186" data-mp="3ksv5r2sf0jku">
                        <div className="h-full bg-[#4F46E5]" style={{
                      width: `${row.open}%`
                    }} data-id="element-187" data-mp="3fu1uzi4ij6od" />
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3" data-id="element-188" data-mp="36un108p50dz4">
                    <div className="flex items-center gap-2" data-id="element-189" data-mp="1mbb9ije0gfje">
                      <span className="w-10" data-id="element-190" data-mp="1marnaqhbv4zk">{row.interact} %</span>
                      <div className="w-24 h-2 bg-slate-100 rounded-full overflow-hidden" data-id="element-191" data-mp="1qc4vh77w7k9n">
                        <div className="h-full bg-emerald-500" style={{
                      width: `${row.interact}%`
                    }} data-id="element-192" data-mp="1vbhf7j3gwb80" />
                      </div>
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </section>

      {/* Coverage & funnel */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-193" data-mp="1clrn53r3a3js">
        <section className="bg-white p-6 rounded-xl shadow-sm border border-slate-100 flex flex-col" data-id="element-194" data-mp="1ck6qn8l7saja">
          <h2 className="text-lg font-bold text-[#0F172A] font-heading mb-4" data-id="element-195" data-mp="1heb141tov0p4">
            {t('Couverture géographique', 'Geographic coverage')}
          </h2>

          <div className="flex gap-2 mb-6 overflow-x-auto pb-2" data-id="element-196" data-mp="ouvqntbouk67">
            {sectors.map(s => <button key={s.id} onClick={() => setSelectedSector(s.id)} className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors border ${selectedSector === s.id ? 'bg-[#0F172A] text-white border-[#0F172A]' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`} data-id="element-197" data-mp="3ef0jxu19cnl2">
                <span className="flex items-center gap-2" data-id="element-198" data-mp="154b4w28edzj">
                  <span className={`w-2 h-2 rounded-full ${s.status === 'green' ? 'bg-emerald-500' : s.status === 'yellow' ? 'bg-amber-400' : 'bg-red-500'}`} data-id="element-199" data-mp="3scyuspgde1vt" />
                  {sectorName(s.id)}
                </span>
              </button>)}
          </div>

          <div className="flex-1 bg-slate-50 rounded-xl border border-slate-100 p-5 flex flex-col justify-center" data-id="element-200" data-mp="ykx52lhiz81l">
            <div className="flex items-center gap-3 mb-4 flex-wrap" data-id="element-201" data-mp="13k9yxq6tdfdo">
              <MapPinIcon className="w-6 h-6 text-[#4F46E5]" data-id="element-202" data-mp="omhohcg4jh6c" />
              <h3 className="text-xl font-bold text-[#0F172A]" data-id="element-203" data-mp="oly3tnz3aeom">
                {sectorName(active.id)}
              </h3>
              <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${active.status === 'green' ? 'bg-emerald-100 text-emerald-700' : active.status === 'yellow' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`} data-id="element-204" data-mp="tnnfn5u4ywdr">
                {active.status === 'green' ? t('Forte portée', 'Strong reach') : active.status === 'yellow' ? t('Portée moyenne', 'Average reach') : t('Faible portée', 'Low reach')}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-4" data-id="element-205" data-mp="2k22wkdky283g">
              {[{
              label: t('Citoyens inscrits', 'Registered citizens'),
              value: active.citizens.toLocaleString(locale)
            }, {
              label: t('Cartes bciti+ actives', 'Active bciti+ cards'),
              value: active.cards.toLocaleString(locale)
            }, {
              label: t("Taux d'ouverture moy.", 'Avg. open rate'),
              value: `${active.openRate} %`
            }, {
              label: t('Avis envoyés', 'Notices sent'),
              value: active.notices.toLocaleString(locale)
            }].map(item => <div key={item.label} className="bg-white p-3 rounded-lg border border-slate-200 shadow-sm" data-id="element-206" data-mp="3mo4q3lhgqfy8">
                  <div className="text-xs text-slate-500 mb-1" data-id="element-207" data-mp="1f1d12bzccvu1">{item.label}</div>
                  <div className="font-bold text-lg text-[#0F172A]" data-id="element-208" data-mp="1ez0chfz2vn80">{item.value}</div>
                </div>)}
              <div className="col-span-2 bg-indigo-50 p-3 rounded-lg border border-indigo-100 shadow-sm flex justify-between items-center gap-3" data-id="element-209" data-mp="160ew9rh2wkae">
                <div className="text-sm font-medium text-indigo-900" data-id="element-210" data-mp="1134z1png74c0">
                  {t("Questions posées à l'IA", 'Questions asked to the AI')}
                </div>
                <div className="font-bold text-xl text-[#4F46E5]" data-id="element-211" data-mp="1tpzgsesvrpls">
                  {active.questions.toLocaleString(locale)}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-white p-6 rounded-xl shadow-sm border border-slate-100" data-id="element-212" data-mp="1jzmzcvgd4cjv">
          <h2 className="text-lg font-bold text-[#0F172A] font-heading mb-6" data-id="element-213" data-mp="2hgvh60bejt4n">
            {t('Entonnoir de communication', 'Communication funnel')}
          </h2>
          <div className="space-y-2" data-id="element-214" data-mp="arjnpas7a6dc">
            {funnel.map(step => <div key={step.label} data-id="element-215" data-mp="ato2inrn0pse">
                <div className="flex items-center justify-between mb-1 px-1 gap-3" data-id="element-216" data-mp="etohqamk950i">
                  <span className="text-sm font-medium text-slate-700" data-id="element-217" data-mp="11rw8pfedtd3">
                    {step.label}
                  </span>
                  <span className="text-sm font-bold text-slate-900" data-id="element-218" data-mp="5ztb0y5sj6ir">
                    {step.value}
                  </span>
                </div>
                <div className="w-full h-8 bg-slate-100 rounded-lg overflow-hidden flex items-center" data-id="element-219" data-mp="3nbb6uab2fas7">
                  <div className={`h-full ${step.color} flex items-center justify-end px-3 transition-all duration-1000`} style={{
                width: `${step.percent}%`
              }} data-id="element-220" data-mp="3s5ovzuz4wlof">
                    <span className="text-xs font-bold text-white drop-shadow-md" data-id="element-221" data-mp="1ch6qhlg8274i">
                      {step.percent} %
                    </span>
                  </div>
                </div>
              </div>)}
          </div>
        </section>
      </div>

      {/* Topics & questions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-222" data-mp="1mldojeit8ndp">
        <section className="bg-white p-6 rounded-xl shadow-sm border border-slate-100" data-id="element-223" data-mp="1mir6um27grhr">
          <h2 className="text-lg font-bold text-[#0F172A] font-heading mb-6" data-id="element-224" data-mp="20djts5kfd9yd">
            {t('Sujets les plus engageants', 'Most engaging topics')}
          </h2>
          <div className="space-y-4" data-id="element-225" data-mp="2a9n25t5ve4tr">
            {topics.map((item, i) => <div key={item.topic} className="flex items-center justify-between gap-3 p-3 hover:bg-slate-50 rounded-lg transition-colors border border-transparent hover:border-slate-100" data-id="element-226" data-mp="25bv1oar5tady" data-mpi={i}>
                <div className="flex items-center gap-3 min-w-0" data-id="element-227" data-mp="2fau9xs80j40f">
                  <div className="w-6 h-6 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center text-xs font-bold flex-shrink-0" data-id="element-228" data-mp="owk1vb1zox4v">
                    {i + 1}
                  </div>
                  <div className="min-w-0" data-id="element-229" data-mp="md6zlpxh95zl">
                    <div className="font-medium text-slate-900 truncate" data-id="element-230" data-mp="mcdz36p43cpw">
                      {item.topic}
                    </div>
                    <div className="text-xs text-slate-500" data-id="element-231" data-mp="vdliyjqvsb0g">
                      {item.sent} {t('avis envoyés', 'notices sent')}
                    </div>
                  </div>
                </div>
                <div className="flex gap-4 text-right flex-shrink-0" data-id="element-232" data-mp="3mfpun9kvdko">
                  <div data-id="element-233" data-mp="3k319r9be4yn">
                    <div className="text-xs text-slate-500" data-id="element-234" data-mp="7nhszf6m89rx">
                      {t('Ouverture', 'Open')}
                    </div>
                    <div className="font-medium text-[#0F172A]" data-id="element-235" data-mp="clt03i5j066g">{item.open}</div>
                  </div>
                  <div data-id="element-236" data-mp="hecg0lrordef">
                    <div className="text-xs text-slate-500" data-id="element-237" data-mp="hevnf8jpmje7">{t('Clics', 'Clicks')}</div>
                    <div className="font-medium text-[#0F172A]" data-id="element-238" data-mp="3g31flvcs6ty0">{item.click}</div>
                  </div>
                  <div className="bg-indigo-50 px-2 py-1 rounded text-center min-w-[3rem]" data-id="element-239" data-mp="3l0uncstqfw0e">
                    <div className="text-[10px] text-[#4F46E5] font-bold uppercase" data-id="element-240" data-mp="3dimqz9lsytvp">
                      {t('Questions', 'Questions')}
                    </div>
                    <div className="font-bold text-[#4F46E5]" data-id="element-241" data-mp="33k78tw4r6uyc">{item.q}</div>
                  </div>
                </div>
              </div>)}
          </div>
        </section>

        <section className="bg-white p-6 rounded-xl shadow-sm border border-slate-100 flex flex-col" data-id="element-242" data-mp="2uk0phtmdj0r9">
          <div className="flex items-center gap-2 mb-6" data-id="element-243" data-mp="2pt24whucrgi7">
            <MessageSquareIcon className="w-5 h-5 text-[#4F46E5]" data-id="element-244" data-mp="2prh9kq07rtja" />
            <h2 className="text-lg font-bold text-[#0F172A] font-heading" data-id="element-245" data-mp="2kvinxa69spmk">
              {t('Insights ATNA : questions fréquentes', 'ATNA insights: frequent questions')}
            </h2>
          </div>

          <div className="bg-slate-50 rounded-xl p-1 flex-1" data-id="element-246" data-mp="26uptepfyxujg">
            <ul className="space-y-1" data-id="element-247" data-mp="26v7ymbklzj3i">
              {questions.map((q, i) => <li key={q} className="flex items-center gap-3 p-3 bg-white rounded-lg shadow-sm" data-id="element-248" data-mp="1x6asl86nh03f" data-mpi={i}>
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${i < 3 ? 'bg-[#4F46E5] text-white' : 'bg-slate-100 text-slate-500'}`} data-id="element-249" data-mp="1n8wdpd0dkjd8">
                    {i + 1}
                  </span>
                  <span className="text-sm font-medium text-slate-700" data-id="element-250" data-mp="2kwy4e4jos9v9">{q}</span>
                  {i < 3 && <TrendingUpIcon className="w-4 h-4 text-emerald-500 ml-auto flex-shrink-0" data-id="element-251" data-mp="266qr038jdh1f" />}
                </li>)}
            </ul>
          </div>
        </section>
      </div>
    </div>;
}